import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SEO, breadcrumbJsonLd, faqJsonLd } from "../components/SEO";
import { Layout, Breadcrumbs } from "../components/Layout";
import { SITE_URL, SITE_NAME, LOGO } from "../lib/api";
import { GUIDES, GUIDE_ORDER } from "../lib/guides";
import { lp, lpFull } from "../lib/urls";

export const GUIDE_LANGS = ["es", "it", "de", "pt", "fr", "nl", "ru", "lt"];

const GP_LABELS = {
  en: { guide: "Guide", min: "Min Read", faq: "FAQ", related: "Related", notFound: "Guide not found", all: "All guides" },
  de: { guide: "Ratgeber", min: "Min. Lesezeit", faq: "FAQ", related: "Verwandte Themen", notFound: "Ratgeber nicht gefunden", all: "Alle Ratgeber" },
  es: { guide: "Guía", min: "Min de lectura", faq: "Preguntas frecuentes", related: "Relacionado", notFound: "Guía no encontrada", all: "Todas las guías" },
  it: { guide: "Guida", min: "Min di lettura", faq: "Domande frequenti", related: "Correlati", notFound: "Guida non trovata", all: "Tutte le guide" },
  pt: { guide: "Guia", min: "Min de leitura", faq: "Perguntas frequentes", related: "Relacionado", notFound: "Guia não encontrado", all: "Todos os guias" },
  fr: { guide: "Guide", min: "Min de lecture", faq: "Questions fréquentes", related: "Sur le même sujet", notFound: "Guide introuvable", all: "Tous les guides" },
  nl: { guide: "Gids", min: "Min leestijd", faq: "Veelgestelde vragen", related: "Gerelateerd", notFound: "Gids niet gevonden", all: "Alle gidsen" },
  ru: { guide: "Руководство", min: "мин чтения", faq: "Вопросы и ответы", related: "По теме", notFound: "Руководство не найдено", all: "Все руководства" },
  lt: { guide: "Gidas", min: "min skaitymo", faq: "Klausimai ir atsakymai", related: "Susiję", notFound: "Gidas nerastas", all: "Visi gidai" },
};

export const useGuideTranslations = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const [dict, setDict] = useState(null);
  useEffect(() => {
    setDict(null);
    if (lang !== "en" && GUIDE_LANGS.includes(lang)) {
      import(`../lib/guides-i18n/${lang}.json`)
        .then((m) => setDict(m.default || m))
        .catch(() => setDict(null));
    }
  }, [lang]);
  return dict;
};

export default function GuidePage() {
  const { guideSlug } = useParams();
  const { t, i18n } = useTranslation();
  const dict = useGuideTranslations();
  const gl = GP_LABELS[(i18n.language || "en").slice(0, 2)] || GP_LABELS.en;
  const base = GUIDES[guideSlug];
  const g = base
    ? dict?.[guideSlug]
      ? { ...base, ...dict[guideSlug] }
      : base
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [guideSlug]);

  if (!g) {
    return (
      <Layout>
        <div className="p-14 bg-white text-center">
          <h1 className="font-display font-bold text-3xl">{gl.notFound}</h1>
          <Link
            to={lp("/guides")}
            className="text-yellow-600 underline mt-4 inline-block"
          >
            {gl.all} →
          </Link>
        </div>
      </Layout>
    );
  }

  const crumbs = [
    { name: t("qa2.home"), path: "/" },
    { name: t("gh.crumb"), path: "/guides" },
    { name: g.title },
  ];
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.title,
    description: g.desc,
    datePublished: "2026-01-15",
    dateModified: "2026-06-01",
    inLanguage: (i18n.language || "en").slice(0, 2),
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}${LOGO}` },
    },
    mainEntityOfPage: `${SITE_URL}${lpFull(`/guides/${guideSlug}`)}`,
  };

  return (
    <Layout>
      <SEO
        title={g.metaTitle}
        description={g.desc}
        path={`/guides/${guideSlug}`}
        type="article"
        altLangs={GUIDE_LANGS}
        jsonLd={[
          breadcrumbJsonLd(
            crumbs.map((c) => ({
              name: c.name,
              path: c.path || `/guides/${guideSlug}`,
            })),
          ),
          articleLd,
          faqJsonLd(g.faqs),
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <article className="bg-white border-b border-border">
        <header className="p-6 sm:p-14 border-b border-border">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
            {gl.guide} · {g.minutes} {gl.min}
          </p>
          <h1
            className="font-display font-black uppercase text-3xl sm:text-5xl tracking-tighter mb-4 text-gray-950 max-w-4xl"
            data-testid="guide-title"
          >
            {g.title}
          </h1>
          <p className="text-gray-600 max-w-3xl">{g.desc}</p>
        </header>
        <div className="p-6 sm:p-14 max-w-4xl">
          {g.sections.map((s, i) => (
            <section key={s.h} className="mb-10">
              <h2 className="font-display font-black uppercase text-xl sm:text-2xl tracking-tight text-gray-950 mb-3">
                <span className="font-mono-tech text-xs bg-yellow-500 text-black px-1.5 py-0.5 mr-3 align-middle">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.h}
              </h2>
              {s.ps.map((p) => (
                <p
                  key={p.slice(0, 24)}
                  className="text-gray-700 leading-relaxed mb-3"
                >
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>
        <div className="bg-[#0A0A0A] text-white px-6 sm:px-14 py-8 border-t-2 border-yellow-500">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-500 mb-2">
            Need_Parts?
          </p>
          <p className="text-gray-300 max-w-2xl mb-4">
            VANROCK manufactures OEM-specification undercarriage parts for
            9,000+ machine models — direct manufacturer pricing.
          </p>
          <Link
            to={lp("/search")}
            data-testid="guide-cta-parts"
            className="inline-block bg-yellow-500 text-black font-mono-tech text-xs uppercase tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors"
          >
            Browse undercarriage parts →
          </Link>
        </div>
        <section className="p-6 sm:p-14 border-t border-border">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-4">
            {gl.faq}
          </p>
          <div className="max-w-3xl divide-y divide-border">
            {g.faqs.map((f) => (
              <details key={f.q} className="py-4 group">
                <summary className="cursor-pointer font-semibold text-gray-950 list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-yellow-600 font-mono-tech ml-4 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="text-sm text-gray-600 mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
        <section className="p-6 sm:p-14 border-t border-border">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-4">
            {gl.related}
          </p>
          <div className="flex flex-wrap gap-2">
            {g.related.map((r) => (
              <Link
                key={r.path}
                to={lp(r.path)}
                className="border border-border px-3 py-1.5 text-sm text-gray-700 hover:bg-yellow-500 hover:text-gray-950 hover:border-gray-950 transition-colors"
              >
                {r.name} →
              </Link>
            ))}
            {GUIDE_ORDER.filter((s) => s !== guideSlug)
              .slice(0, 2)
              .map((s) => (
                <Link
                  key={s}
                  to={lp(`/guides/${s}`)}
                  className="border border-border px-3 py-1.5 text-sm text-gray-700 hover:bg-yellow-500 hover:text-gray-950 hover:border-gray-950 transition-colors"
                >
                  {dict?.[s]?.title || GUIDES[s].title} →
                </Link>
              ))}
          </div>
        </section>
      </article>
    </Layout>
  );
}
